/**
 * Vias Element Colors Store — user-customisable colors per element type
 *
 * @module core/modules/vias/store/element-colors-store
 *
 * @description
 * Zustand store with localStorage persistence. Keeps only user overrides;
 * falls back to VIAS_ELEMENT_COLORS defaults for untouched entries.
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { VIAS_ELEMENT_COLORS, type ViasElementId } from '../constants/element-colors';

interface ViasElementColorsState {
  /** Only colours the user has explicitly changed */
  overrides: Partial<Record<ViasElementId, string>>;
}

interface ViasElementColorsActions {
  /** Return override if set, otherwise default */
  getColor: (id: ViasElementId) => string;
  /** Set a user override */
  setColor: (id: ViasElementId, hex: string) => void;
  /** Remove a single override (revert to default) */
  resetColor: (id: ViasElementId) => void;
  /** Remove all overrides */
  resetAll: () => void;
}

export type ViasElementColorsStore = ViasElementColorsState & ViasElementColorsActions;

export const useViasElementColorsStore = create<ViasElementColorsStore>()(
  devtools(
    persist(
      (set, get) => ({
        overrides: {},

        getColor(id: ViasElementId): string {
          return get().overrides[id] ?? VIAS_ELEMENT_COLORS[id];
        },

        setColor(id: ViasElementId, hex: string): void {
          set(
            (s) => ({ overrides: { ...s.overrides, [id]: hex } }),
            false,
            'setColor',
          );
        },

        resetColor(id: ViasElementId): void {
          set(
            (s) => {
              const next = { ...s.overrides };
              delete next[id];
              return { overrides: next };
            },
            false,
            'resetColor',
          );
        },

        resetAll(): void {
          set({ overrides: {} }, false, 'resetAll');
        },
      }),
      { name: 'vias-element-colors' },
    ),
    { name: 'ViasElementColors' },
  ),
);
